// ============================================================================
// Animal Definitions
// ============================================================================
import { ItemType } from '../items/types.js';
import { ActivityPeriod, AggressionLevel, AnimalAttackType, AnimalSize, BehaviorType, DietType, HabitatType, NpcCategory, NpcType, SensePriority, SocialType, ThreatResponse, } from './types.js';
// ============================================================================
// Animal Definitions Map
// ============================================================================
export const NPC_DEFINITIONS = new Map([
    // =========================================================================
    // SMALL PREY
    // =========================================================================
    [
        NpcType.RABBIT,
        {
            type: NpcType.RABBIT,
            name: 'Rabbit',
            category: NpcCategory.PREY,
            size: AnimalSize.SMALL,
            baseHealth: 15,
            moveSpeed: 1.2,
            runSpeedMultiplier: 2.5,
            attackTypes: [AnimalAttackType.BITE, AnimalAttackType.KICK],
            attacks: [
                {
                    type: AnimalAttackType.BITE,
                    damage: 1,
                    damageType: 0,
                    cooldown: 60,
                    range: 0.5,
                    accuracy: 0.7,
                },
                {
                    type: AnimalAttackType.KICK,
                    damage: 2,
                    damageType: 0,
                    cooldown: 80,
                    range: 0.5,
                    accuracy: 0.6,
                },
            ],
            baseDamage: 1,
            attackCooldown: 60,
            armorValue: 0,
            diet: DietType.HERBIVORE,
            habitat: HabitatType.LAND_ONLY,
            social: SocialType.FAMILY,
            activityPeriod: ActivityPeriod.CREPUSCULAR,
            aggression: AggressionLevel.PASSIVE,
            threatResponse: ThreatResponse.FLEE,
            traits: [BehaviorType.COWARDLY, BehaviorType.FAST_RUNNER],
            sightRange: 8,
            hearingRange: 12,
            smellRange: 6,
            primarySense: SensePriority.HEARING,
            hungerRate: 0.0015,
            thirstRate: 0.001,
            sleepHoursPerDay: 8,
            minPackSize: 1,
            maxPackSize: 8,
            territoryRadius: 10,
            fearThreshold: 0.2,
            fleeDistance: 15,
            courageBonus: 0,
            humanFearFactor: 0.9,
            combatPower: 2,
            aggroDistance: 0,
            breedingSeason: true,
            gestationTicks: 30 * 20 * 60, // ~30 days
            offspringCount: [3, 8],
            spriteSize: 0.6,
            animationSpeedMultiplier: 1.5,
        },
    ],
    [
        NpcType.SQUIRREL,
        {
            type: NpcType.SQUIRREL,
            name: 'Squirrel',
            category: NpcCategory.PREY,
            size: AnimalSize.SMALL,
            baseHealth: 10,
            moveSpeed: 1.5,
            runSpeedMultiplier: 2.8,
            attackTypes: [AnimalAttackType.BITE],
            attacks: [
                {
                    type: AnimalAttackType.BITE,
                    damage: 1,
                    damageType: 0,
                    cooldown: 40,
                    range: 0.3,
                    accuracy: 0.6,
                },
            ],
            baseDamage: 1,
            attackCooldown: 40,
            armorValue: 0,
            diet: DietType.HERBIVORE,
            habitat: HabitatType.ARBOREAL,
            social: SocialType.SOLITARY,
            activityPeriod: ActivityPeriod.DIURNAL,
            aggression: AggressionLevel.PASSIVE,
            threatResponse: ThreatResponse.FLEE,
            traits: [BehaviorType.COWARDLY, BehaviorType.AGILE],
            sightRange: 10,
            hearingRange: 8,
            smellRange: 4,
            primarySense: SensePriority.SIGHT,
            hungerRate: 0.002,
            thirstRate: 0.001,
            sleepHoursPerDay: 12,
            minPackSize: 1,
            maxPackSize: 1,
            territoryRadius: 15,
            fearThreshold: 0.15,
            fleeDistance: 20,
            courageBonus: 0,
            humanFearFactor: 0.85,
            combatPower: 1,
            aggroDistance: 0,
            breedingSeason: true,
            gestationTicks: 40 * 20 * 60,
            offspringCount: [2, 5],
            spriteSize: 0.5,
            animationSpeedMultiplier: 1.8,
        },
    ],
    // =========================================================================
    // MEDIUM PREY
    // =========================================================================
    [
        NpcType.DEER,
        {
            type: NpcType.DEER,
            name: 'Deer',
            category: NpcCategory.PREY,
            size: AnimalSize.MEDIUM,
            baseHealth: 50,
            moveSpeed: 1.0,
            runSpeedMultiplier: 2.2,
            attackTypes: [AnimalAttackType.KICK, AnimalAttackType.GORE],
            attacks: [
                {
                    type: AnimalAttackType.KICK,
                    damage: 8,
                    damageType: 0,
                    cooldown: 45,
                    range: 1.0,
                    accuracy: 0.7,
                },
                {
                    type: AnimalAttackType.GORE,
                    damage: 12,
                    damageType: 0,
                    cooldown: 60,
                    range: 0.8,
                    accuracy: 0.6,
                },
            ],
            baseDamage: 5,
            attackCooldown: 45,
            armorValue: 0,
            diet: DietType.HERBIVORE,
            habitat: HabitatType.LAND_ONLY,
            social: SocialType.HERD,
            activityPeriod: ActivityPeriod.CREPUSCULAR,
            aggression: AggressionLevel.DEFENSIVE,
            threatResponse: ThreatResponse.FLEE,
            traits: [BehaviorType.ALERT, BehaviorType.FAST_RUNNER],
            sightRange: 15,
            hearingRange: 20,
            smellRange: 12,
            primarySense: SensePriority.HEARING,
            hungerRate: 0.001,
            thirstRate: 0.0008,
            sleepHoursPerDay: 4,
            minPackSize: 2,
            maxPackSize: 12,
            territoryRadius: 50,
            fearThreshold: 0.3,
            fleeDistance: 30,
            courageBonus: 0.1,
            humanFearFactor: 0.75,
            combatPower: 15,
            aggroDistance: 0,
            breedingSeason: true,
            gestationTicks: 200 * 20 * 60,
            offspringCount: [1, 2],
            spriteSize: 1.0,
            animationSpeedMultiplier: 1.0,
        },
    ],
    [
        NpcType.BOAR,
        {
            type: NpcType.BOAR,
            name: 'Wild Boar',
            category: NpcCategory.PREY,
            size: AnimalSize.MEDIUM,
            baseHealth: 70,
            moveSpeed: 0.8,
            runSpeedMultiplier: 1.8,
            attackTypes: [AnimalAttackType.GORE, AnimalAttackType.BITE, AnimalAttackType.CHARGE],
            attacks: [
                {
                    type: AnimalAttackType.GORE,
                    damage: 15,
                    damageType: 0,
                    cooldown: 35,
                    range: 0.8,
                    accuracy: 0.75,
                },
                {
                    type: AnimalAttackType.BITE,
                    damage: 10,
                    damageType: 0,
                    cooldown: 30,
                    range: 0.5,
                    accuracy: 0.8,
                },
                {
                    type: AnimalAttackType.CHARGE,
                    damage: 20,
                    damageType: 0,
                    cooldown: 80,
                    range: 2.0,
                    accuracy: 0.65,
                },
            ],
            baseDamage: 12,
            attackCooldown: 35,
            armorValue: 2,
            diet: DietType.OMNIVORE,
            habitat: HabitatType.LAND_ONLY,
            social: SocialType.FAMILY,
            activityPeriod: ActivityPeriod.NOCTURNAL,
            aggression: AggressionLevel.TERRITORIAL,
            threatResponse: ThreatResponse.FIGHT_OR_FLIGHT,
            traits: [BehaviorType.AGGRESSIVE, BehaviorType.TERRITORIAL],
            sightRange: 8,
            hearingRange: 15,
            smellRange: 25,
            primarySense: SensePriority.SMELL,
            hungerRate: 0.0012,
            thirstRate: 0.001,
            sleepHoursPerDay: 8,
            minPackSize: 1,
            maxPackSize: 15,
            territoryRadius: 30,
            fearThreshold: 0.5,
            fleeDistance: 20,
            courageBonus: 0.2,
            humanFearFactor: 0.4,
            combatPower: 35,
            aggroDistance: 8,
            breedingSeason: true,
            gestationTicks: 115 * 20 * 60,
            offspringCount: [4, 8],
            spriteSize: 1.0,
            animationSpeedMultiplier: 0.9,
        },
    ],
    // =========================================================================
    // SMALL PREDATORS
    // =========================================================================
    [
        NpcType.FOX,
        {
            type: NpcType.FOX,
            name: 'Fox',
            category: NpcCategory.PREDATOR,
            size: AnimalSize.SMALL,
            baseHealth: 30,
            moveSpeed: 1.3,
            runSpeedMultiplier: 2.0,
            attackTypes: [AnimalAttackType.BITE],
            attacks: [
                {
                    type: AnimalAttackType.BITE,
                    damage: 6,
                    damageType: 0,
                    cooldown: 30,
                    range: 0.6,
                    accuracy: 0.8,
                },
            ],
            baseDamage: 6,
            attackCooldown: 30,
            armorValue: 0,
            diet: DietType.OMNIVORE,
            habitat: HabitatType.LAND_ONLY,
            social: SocialType.PAIR,
            activityPeriod: ActivityPeriod.CREPUSCULAR,
            aggression: AggressionLevel.OPPORTUNISTIC,
            threatResponse: ThreatResponse.FLEE,
            traits: [BehaviorType.CUNNING, BehaviorType.STEALTHY],
            sightRange: 12,
            hearingRange: 18,
            smellRange: 15,
            primarySense: SensePriority.HEARING,
            hungerRate: 0.0015,
            thirstRate: 0.001,
            sleepHoursPerDay: 10,
            minPackSize: 1,
            maxPackSize: 4,
            territoryRadius: 40,
            fearThreshold: 0.35,
            fleeDistance: 25,
            courageBonus: 0.05,
            humanFearFactor: 0.6,
            combatPower: 12,
            aggroDistance: 4,
            breedingSeason: true,
            gestationTicks: 50 * 20 * 60,
            offspringCount: [3, 6],
            spriteSize: 0.8,
            animationSpeedMultiplier: 1.2,
        },
    ],
    // =========================================================================
    // MEDIUM PREDATORS
    // =========================================================================
    [
        NpcType.WOLF,
        {
            type: NpcType.WOLF,
            name: 'Wolf',
            category: NpcCategory.PREDATOR,
            size: AnimalSize.LARGE,
            baseHealth: 80,
            moveSpeed: 1.1,
            runSpeedMultiplier: 1.9,
            attackTypes: [AnimalAttackType.BITE, AnimalAttackType.CLAW],
            attacks: [
                {
                    type: AnimalAttackType.BITE,
                    damage: 18,
                    damageType: 0,
                    cooldown: 25,
                    range: 0.8,
                    accuracy: 0.85,
                },
                {
                    type: AnimalAttackType.CLAW,
                    damage: 12,
                    damageType: 0,
                    cooldown: 20,
                    range: 0.6,
                    accuracy: 0.8,
                },
            ],
            baseDamage: 15,
            attackCooldown: 25,
            armorValue: 1,
            diet: DietType.CARNIVORE,
            habitat: HabitatType.LAND_ONLY,
            social: SocialType.PACK,
            activityPeriod: ActivityPeriod.CATHEMERAL,
            aggression: AggressionLevel.TERRITORIAL,
            threatResponse: ThreatResponse.CALL_PACK,
            traits: [BehaviorType.PACK_HUNTER, BehaviorType.TERRITORIAL, BehaviorType.INTELLIGENT],
            sightRange: 18,
            hearingRange: 25,
            smellRange: 30,
            primarySense: SensePriority.SMELL,
            hungerRate: 0.0008,
            thirstRate: 0.0005,
            sleepHoursPerDay: 8,
            minPackSize: 2,
            maxPackSize: 12,
            territoryRadius: 80,
            fearThreshold: 0.6,
            fleeDistance: 40,
            courageBonus: 0.25,
            humanFearFactor: 0.3,
            combatPower: 45,
            aggroDistance: 15,
            breedingSeason: true,
            gestationTicks: 63 * 20 * 60,
            offspringCount: [4, 7],
            spriteSize: 1.0,
            animationSpeedMultiplier: 1.0,
            // Night behavior - wolves are more aggressive at night
            nightAggressionBonus: 2.0, // Double aggro range at night
            nightTerritoryPenetration: 10, // Can approach 10 tiles closer to HOME territories at night
        },
    ],
    [
        NpcType.COYOTE,
        {
            type: NpcType.COYOTE,
            name: 'Coyote',
            category: NpcCategory.PREDATOR,
            size: AnimalSize.MEDIUM,
            baseHealth: 50,
            moveSpeed: 1.2,
            runSpeedMultiplier: 2.1,
            attackTypes: [AnimalAttackType.BITE],
            attacks: [
                {
                    type: AnimalAttackType.BITE,
                    damage: 10,
                    damageType: 0,
                    cooldown: 28,
                    range: 0.7,
                    accuracy: 0.8,
                },
            ],
            baseDamage: 8,
            attackCooldown: 28,
            armorValue: 0,
            diet: DietType.OMNIVORE,
            habitat: HabitatType.LAND_ONLY,
            social: SocialType.PAIR,
            activityPeriod: ActivityPeriod.NOCTURNAL,
            aggression: AggressionLevel.OPPORTUNISTIC,
            threatResponse: ThreatResponse.FLEE,
            traits: [BehaviorType.CUNNING, BehaviorType.ADAPTABLE],
            sightRange: 15,
            hearingRange: 20,
            smellRange: 25,
            primarySense: SensePriority.SMELL,
            hungerRate: 0.001,
            thirstRate: 0.0007,
            sleepHoursPerDay: 6,
            minPackSize: 1,
            maxPackSize: 6,
            territoryRadius: 50,
            fearThreshold: 0.4,
            fleeDistance: 30,
            courageBonus: 0.1,
            humanFearFactor: 0.5,
            combatPower: 20,
            aggroDistance: 6,
            breedingSeason: true,
            gestationTicks: 60 * 20 * 60,
            offspringCount: [3, 9],
            spriteSize: 0.9,
            animationSpeedMultiplier: 1.1,
        },
    ],
    // =========================================================================
    // LARGE PREDATORS
    // =========================================================================
    [
        NpcType.BEAR,
        {
            type: NpcType.BEAR,
            name: 'Bear',
            category: NpcCategory.PREDATOR,
            size: AnimalSize.HUGE,
            baseHealth: 200,
            moveSpeed: 0.7,
            runSpeedMultiplier: 1.8,
            attackTypes: [AnimalAttackType.BITE, AnimalAttackType.CLAW, AnimalAttackType.CHARGE],
            attacks: [
                {
                    type: AnimalAttackType.BITE,
                    damage: 35,
                    damageType: 0,
                    cooldown: 40,
                    range: 1.0,
                    accuracy: 0.85,
                },
                {
                    type: AnimalAttackType.CLAW,
                    damage: 30,
                    damageType: 0,
                    cooldown: 35,
                    range: 1.2,
                    accuracy: 0.8,
                },
                {
                    type: AnimalAttackType.CHARGE,
                    damage: 50,
                    damageType: 0,
                    cooldown: 100,
                    range: 3.0,
                    accuracy: 0.7,
                },
            ],
            baseDamage: 35,
            attackCooldown: 40,
            armorValue: 5,
            diet: DietType.OMNIVORE,
            habitat: HabitatType.LAND_ONLY,
            social: SocialType.SOLITARY,
            activityPeriod: ActivityPeriod.DIURNAL,
            aggression: AggressionLevel.AGGRESSIVE,
            threatResponse: ThreatResponse.FIGHT,
            traits: [BehaviorType.PROTECTIVE, BehaviorType.POWERFUL],
            sightRange: 12,
            hearingRange: 15,
            smellRange: 40,
            primarySense: SensePriority.SMELL,
            hungerRate: 0.0006,
            thirstRate: 0.0004,
            sleepHoursPerDay: 16, // Lots of rest, especially before hibernation
            minPackSize: 1,
            maxPackSize: 1,
            territoryRadius: 100,
            fearThreshold: 0.8,
            fleeDistance: 50,
            courageBonus: 0,
            humanFearFactor: 0.2,
            combatPower: 100,
            aggroDistance: 10,
            breedingSeason: true,
            gestationTicks: 220 * 20 * 60,
            offspringCount: [1, 4],
            spriteSize: 1.5,
            animationSpeedMultiplier: 0.8,
        },
    ],
    [
        NpcType.COUGAR,
        {
            type: NpcType.COUGAR,
            name: 'Cougar',
            category: NpcCategory.PREDATOR,
            size: AnimalSize.LARGE,
            baseHealth: 100,
            moveSpeed: 1.0,
            runSpeedMultiplier: 2.5,
            attackTypes: [AnimalAttackType.BITE, AnimalAttackType.CLAW],
            attacks: [
                {
                    type: AnimalAttackType.BITE,
                    damage: 28,
                    damageType: 0,
                    cooldown: 20,
                    range: 0.8,
                    accuracy: 0.9,
                },
                {
                    type: AnimalAttackType.CLAW,
                    damage: 22,
                    damageType: 0,
                    cooldown: 18,
                    range: 0.7,
                    accuracy: 0.85,
                },
            ],
            baseDamage: 25,
            attackCooldown: 20,
            armorValue: 2,
            diet: DietType.CARNIVORE,
            habitat: HabitatType.LAND_ONLY,
            social: SocialType.SOLITARY,
            activityPeriod: ActivityPeriod.CREPUSCULAR,
            aggression: AggressionLevel.OPPORTUNISTIC,
            threatResponse: ThreatResponse.FLEE,
            traits: [BehaviorType.STEALTHY, BehaviorType.AMBUSH_PREDATOR],
            sightRange: 20,
            hearingRange: 18,
            smellRange: 15,
            primarySense: SensePriority.SIGHT,
            hungerRate: 0.0007,
            thirstRate: 0.0005,
            sleepHoursPerDay: 12,
            minPackSize: 1,
            maxPackSize: 1,
            territoryRadius: 120,
            fearThreshold: 0.5,
            fleeDistance: 40,
            courageBonus: 0,
            humanFearFactor: 0.35,
            combatPower: 60,
            aggroDistance: 12,
            breedingSeason: false,
            gestationTicks: 90 * 20 * 60,
            offspringCount: [1, 4],
            spriteSize: 1.2,
            animationSpeedMultiplier: 1.2,
        },
    ],
    // =========================================================================
    // BIRDS - PREY
    // =========================================================================
    [
        NpcType.CHICKEN,
        {
            type: NpcType.CHICKEN,
            name: 'Chicken',
            category: NpcCategory.BIRD,
            size: AnimalSize.SMALL,
            baseHealth: 8,
            moveSpeed: 0.8,
            runSpeedMultiplier: 1.5,
            attackTypes: [AnimalAttackType.PECK],
            attacks: [
                {
                    type: AnimalAttackType.PECK,
                    damage: 1,
                    damageType: 0,
                    cooldown: 50,
                    range: 0.3,
                    accuracy: 0.6,
                },
            ],
            baseDamage: 1,
            attackCooldown: 50,
            armorValue: 0,
            diet: DietType.OMNIVORE,
            habitat: HabitatType.LAND_ONLY,
            social: SocialType.FLOCK,
            activityPeriod: ActivityPeriod.DIURNAL,
            aggression: AggressionLevel.PASSIVE,
            threatResponse: ThreatResponse.FLEE,
            traits: [BehaviorType.COWARDLY, BehaviorType.FLOCKING],
            sightRange: 6,
            hearingRange: 8,
            smellRange: 2,
            primarySense: SensePriority.SIGHT,
            hungerRate: 0.002,
            thirstRate: 0.0015,
            sleepHoursPerDay: 8,
            minPackSize: 3,
            maxPackSize: 20,
            territoryRadius: 10,
            fearThreshold: 0.1,
            fleeDistance: 10,
            courageBonus: 0,
            humanFearFactor: 0.7,
            combatPower: 1,
            aggroDistance: 0,
            breedingSeason: false,
            gestationTicks: 21 * 20 * 60,
            offspringCount: [6, 12],
            spriteSize: 0.5,
            animationSpeedMultiplier: 1.3,
        },
    ],
    [
        NpcType.DUCK,
        {
            type: NpcType.DUCK,
            name: 'Duck',
            category: NpcCategory.BIRD,
            size: AnimalSize.SMALL,
            baseHealth: 12,
            moveSpeed: 0.6,
            runSpeedMultiplier: 1.3,
            attackTypes: [AnimalAttackType.PECK],
            attacks: [
                {
                    type: AnimalAttackType.PECK,
                    damage: 1,
                    damageType: 0,
                    cooldown: 60,
                    range: 0.3,
                    accuracy: 0.5,
                },
            ],
            baseDamage: 1,
            attackCooldown: 60,
            armorValue: 0,
            diet: DietType.OMNIVORE,
            habitat: HabitatType.AMPHIBIOUS,
            social: SocialType.FLOCK,
            activityPeriod: ActivityPeriod.DIURNAL,
            aggression: AggressionLevel.PASSIVE,
            threatResponse: ThreatResponse.FLEE,
            traits: [BehaviorType.COWARDLY, BehaviorType.AQUATIC],
            sightRange: 8,
            hearingRange: 10,
            smellRange: 3,
            primarySense: SensePriority.SIGHT,
            hungerRate: 0.0018,
            thirstRate: 0.001,
            sleepHoursPerDay: 10,
            minPackSize: 2,
            maxPackSize: 15,
            territoryRadius: 20,
            fearThreshold: 0.15,
            fleeDistance: 15,
            courageBonus: 0,
            humanFearFactor: 0.6,
            combatPower: 2,
            aggroDistance: 0,
            breedingSeason: true,
            gestationTicks: 28 * 20 * 60,
            offspringCount: [5, 12],
            spriteSize: 0.6,
            animationSpeedMultiplier: 1.1,
        },
    ],
    // =========================================================================
    // BIRDS - PREDATOR
    // =========================================================================
    [
        NpcType.HAWK,
        {
            type: NpcType.HAWK,
            name: 'Hawk',
            category: NpcCategory.BIRD,
            size: AnimalSize.SMALL,
            baseHealth: 25,
            moveSpeed: 0.5, // Slow on ground
            runSpeedMultiplier: 5.0, // Very fast in flight
            attackTypes: [AnimalAttackType.CLAW, AnimalAttackType.PECK],
            attacks: [
                {
                    type: AnimalAttackType.CLAW,
                    damage: 10,
                    damageType: 0,
                    cooldown: 25,
                    range: 0.5,
                    accuracy: 0.9,
                },
                {
                    type: AnimalAttackType.PECK,
                    damage: 6,
                    damageType: 0,
                    cooldown: 20,
                    range: 0.4,
                    accuracy: 0.85,
                },
            ],
            baseDamage: 8,
            attackCooldown: 25,
            armorValue: 0,
            diet: DietType.CARNIVORE,
            habitat: HabitatType.AERIAL,
            social: SocialType.PAIR,
            activityPeriod: ActivityPeriod.DIURNAL,
            aggression: AggressionLevel.OPPORTUNISTIC,
            threatResponse: ThreatResponse.FLEE,
            traits: [BehaviorType.AERIAL, BehaviorType.KEEN_VISION],
            sightRange: 50, // Excellent vision
            hearingRange: 15,
            smellRange: 2,
            primarySense: SensePriority.SIGHT,
            hungerRate: 0.002,
            thirstRate: 0.001,
            sleepHoursPerDay: 10,
            minPackSize: 1,
            maxPackSize: 2,
            territoryRadius: 200,
            fearThreshold: 0.3,
            fleeDistance: 50,
            courageBonus: 0,
            humanFearFactor: 0.4,
            combatPower: 15,
            aggroDistance: 8,
            breedingSeason: true,
            gestationTicks: 35 * 20 * 60,
            offspringCount: [1, 4],
            spriteSize: 0.7,
            animationSpeedMultiplier: 1.5,
        },
    ],
    [
        NpcType.OWL,
        {
            type: NpcType.OWL,
            name: 'Owl',
            category: NpcCategory.BIRD,
            size: AnimalSize.SMALL,
            baseHealth: 20,
            moveSpeed: 0.4,
            runSpeedMultiplier: 4.0,
            attackTypes: [AnimalAttackType.CLAW, AnimalAttackType.PECK],
            attacks: [
                {
                    type: AnimalAttackType.CLAW,
                    damage: 8,
                    damageType: 0,
                    cooldown: 30,
                    range: 0.5,
                    accuracy: 0.85,
                },
                {
                    type: AnimalAttackType.PECK,
                    damage: 5,
                    damageType: 0,
                    cooldown: 25,
                    range: 0.4,
                    accuracy: 0.8,
                },
            ],
            baseDamage: 6,
            attackCooldown: 30,
            armorValue: 0,
            diet: DietType.CARNIVORE,
            habitat: HabitatType.AERIAL,
            social: SocialType.SOLITARY,
            activityPeriod: ActivityPeriod.NOCTURNAL,
            aggression: AggressionLevel.OPPORTUNISTIC,
            threatResponse: ThreatResponse.FLEE,
            traits: [BehaviorType.NOCTURNAL, BehaviorType.STEALTHY, BehaviorType.AERIAL],
            sightRange: 35,
            hearingRange: 40, // Excellent hearing
            smellRange: 1,
            primarySense: SensePriority.HEARING,
            hungerRate: 0.0015,
            thirstRate: 0.0008,
            sleepHoursPerDay: 12,
            minPackSize: 1,
            maxPackSize: 1,
            territoryRadius: 150,
            fearThreshold: 0.25,
            fleeDistance: 40,
            courageBonus: 0,
            humanFearFactor: 0.45,
            combatPower: 12,
            aggroDistance: 6,
            breedingSeason: true,
            gestationTicks: 30 * 20 * 60,
            offspringCount: [2, 5],
            spriteSize: 0.6,
            animationSpeedMultiplier: 1.2,
        },
    ],
    // =========================================================================
    // REPTILES
    // =========================================================================
    [
        NpcType.SNAKE,
        {
            type: NpcType.SNAKE,
            name: 'Snake',
            category: NpcCategory.PREDATOR,
            size: AnimalSize.SMALL,
            baseHealth: 15,
            moveSpeed: 0.6,
            runSpeedMultiplier: 1.5,
            attackTypes: [AnimalAttackType.BITE, AnimalAttackType.VENOM],
            attacks: [
                {
                    type: AnimalAttackType.BITE,
                    damage: 4,
                    damageType: 0,
                    cooldown: 40,
                    range: 0.6,
                    accuracy: 0.85,
                },
                {
                    type: AnimalAttackType.VENOM,
                    damage: 2,
                    damageType: 1,
                    cooldown: 60,
                    range: 0.6,
                    accuracy: 0.75,
                },
            ],
            baseDamage: 4,
            attackCooldown: 40,
            armorValue: 0,
            diet: DietType.CARNIVORE,
            habitat: HabitatType.LAND_ONLY,
            social: SocialType.SOLITARY,
            activityPeriod: ActivityPeriod.DIURNAL,
            aggression: AggressionLevel.DEFENSIVE,
            threatResponse: ThreatResponse.BLUFF,
            traits: [BehaviorType.STEALTHY, BehaviorType.VENOMOUS],
            sightRange: 6,
            hearingRange: 3,
            smellRange: 15,
            primarySense: SensePriority.HEAT,
            hungerRate: 0.0003, // Very slow metabolism
            thirstRate: 0.0002,
            sleepHoursPerDay: 16,
            minPackSize: 1,
            maxPackSize: 1,
            territoryRadius: 20,
            fearThreshold: 0.4,
            fleeDistance: 10,
            courageBonus: 0,
            humanFearFactor: 0.5,
            combatPower: 8,
            aggroDistance: 3,
            breedingSeason: true,
            gestationTicks: 60 * 20 * 60,
            offspringCount: [5, 20],
            spriteSize: 0.7,
            animationSpeedMultiplier: 0.8,
        },
    ],
    [
        NpcType.CROCODILE,
        {
            type: NpcType.CROCODILE,
            name: 'Crocodile',
            category: NpcCategory.PREDATOR,
            size: AnimalSize.HUGE,
            baseHealth: 180,
            moveSpeed: 0.3, // Slow on land
            runSpeedMultiplier: 3.0, // Fast lunge
            attackTypes: [AnimalAttackType.BITE, AnimalAttackType.TAIL_WHIP],
            attacks: [
                {
                    type: AnimalAttackType.BITE,
                    damage: 50,
                    damageType: 0,
                    cooldown: 50,
                    range: 1.2,
                    accuracy: 0.9,
                },
                {
                    type: AnimalAttackType.TAIL_WHIP,
                    damage: 25,
                    damageType: 0,
                    cooldown: 40,
                    range: 1.5,
                    accuracy: 0.75,
                },
            ],
            baseDamage: 40,
            attackCooldown: 50,
            armorValue: 8,
            diet: DietType.CARNIVORE,
            habitat: HabitatType.AMPHIBIOUS,
            social: SocialType.SOLITARY,
            activityPeriod: ActivityPeriod.CATHEMERAL,
            aggression: AggressionLevel.OPPORTUNISTIC,
            threatResponse: ThreatResponse.FIGHT,
            traits: [BehaviorType.AMBUSH_PREDATOR, BehaviorType.AQUATIC, BehaviorType.ARMORED],
            sightRange: 15,
            hearingRange: 10,
            smellRange: 8,
            primarySense: SensePriority.VIBRATION,
            hungerRate: 0.0002, // Very slow metabolism
            thirstRate: 0.0001,
            sleepHoursPerDay: 17,
            minPackSize: 1,
            maxPackSize: 1,
            territoryRadius: 40,
            fearThreshold: 0.9,
            fleeDistance: 20,
            courageBonus: 0,
            humanFearFactor: 0.1,
            combatPower: 90,
            aggroDistance: 8,
            breedingSeason: true,
            gestationTicks: 90 * 20 * 60,
            offspringCount: [20, 60],
            spriteSize: 2.0,
            animationSpeedMultiplier: 0.6,
        },
    ],
    // =========================================================================
    // AQUATIC
    // =========================================================================
    [
        NpcType.FISH,
        {
            type: NpcType.FISH,
            name: 'Fish',
            category: NpcCategory.AQUATIC,
            size: AnimalSize.SMALL,
            baseHealth: 5,
            moveSpeed: 1.0,
            runSpeedMultiplier: 2.0,
            attackTypes: [],
            attacks: [],
            baseDamage: 0,
            attackCooldown: 100,
            armorValue: 0,
            diet: DietType.OMNIVORE,
            habitat: HabitatType.WATER_ONLY,
            social: SocialType.FLOCK,
            activityPeriod: ActivityPeriod.DIURNAL,
            aggression: AggressionLevel.PASSIVE,
            threatResponse: ThreatResponse.FLEE,
            traits: [BehaviorType.AQUATIC, BehaviorType.FLOCKING],
            sightRange: 8,
            hearingRange: 5,
            smellRange: 10,
            primarySense: SensePriority.VIBRATION,
            hungerRate: 0.001,
            thirstRate: 0,
            sleepHoursPerDay: 8,
            minPackSize: 5,
            maxPackSize: 100,
            territoryRadius: 20,
            fearThreshold: 0.1,
            fleeDistance: 15,
            courageBonus: 0,
            humanFearFactor: 0.8,
            combatPower: 0,
            aggroDistance: 0,
            breedingSeason: true,
            gestationTicks: 7 * 20 * 60,
            offspringCount: [100, 1000],
            spriteSize: 0.4,
            animationSpeedMultiplier: 1.5,
        },
    ],
    [
        NpcType.FROG,
        {
            type: NpcType.FROG,
            name: 'Frog',
            category: NpcCategory.AQUATIC,
            size: AnimalSize.TINY,
            baseHealth: 5,
            moveSpeed: 0.5,
            runSpeedMultiplier: 3.0, // Good at hopping
            attackTypes: [],
            attacks: [],
            baseDamage: 0,
            attackCooldown: 100,
            armorValue: 0,
            diet: DietType.INSECTIVORE,
            habitat: HabitatType.AMPHIBIOUS,
            social: SocialType.SOLITARY,
            activityPeriod: ActivityPeriod.NOCTURNAL,
            aggression: AggressionLevel.PASSIVE,
            threatResponse: ThreatResponse.FLEE,
            traits: [BehaviorType.AQUATIC, BehaviorType.COWARDLY],
            sightRange: 6,
            hearingRange: 10,
            smellRange: 3,
            primarySense: SensePriority.SIGHT,
            hungerRate: 0.002,
            thirstRate: 0.003, // Need moisture
            sleepHoursPerDay: 12,
            minPackSize: 1,
            maxPackSize: 1,
            territoryRadius: 5,
            fearThreshold: 0.1,
            fleeDistance: 8,
            courageBonus: 0,
            humanFearFactor: 0.9,
            combatPower: 0,
            aggroDistance: 0,
            breedingSeason: true,
            gestationTicks: 14 * 20 * 60,
            offspringCount: [100, 500],
            spriteSize: 0.3,
            animationSpeedMultiplier: 1.8,
        },
    ],
    // =========================================================================
    // UNDEAD
    // =========================================================================
    [
        NpcType.SKELETON,
        {
            type: NpcType.SKELETON,
            name: 'Skeleton',
            category: NpcCategory.UNDEAD,
            size: AnimalSize.MEDIUM,
            baseHealth: 40,
            moveSpeed: 0.85,
            runSpeedMultiplier: 1.3,
            attackTypes: [AnimalAttackType.CLAW, AnimalAttackType.BITE],
            attacks: [
                {
                    type: AnimalAttackType.CLAW,
                    damage: 8,
                    damageType: 1, // Sharp
                    cooldown: 40,
                    range: 1.2,
                    accuracy: 0.75,
                },
                {
                    type: AnimalAttackType.BITE,
                    damage: 5,
                    damageType: 0, // Blunt
                    cooldown: 50,
                    range: 0.8,
                    accuracy: 0.7,
                },
            ],
            baseDamage: 8,
            attackCooldown: 40,
            armorValue: 2,
            diet: DietType.NONE, // Undead don't eat
            habitat: HabitatType.LAND_ONLY,
            social: SocialType.PACK,
            activityPeriod: ActivityPeriod.NOCTURNAL,
            aggression: AggressionLevel.AGGRESSIVE, // Always hostile
            threatResponse: ThreatResponse.FIGHT, // Never flee
            traits: [BehaviorType.AGGRESSIVE, BehaviorType.TERRITORIAL],
            sightRange: 12,
            hearingRange: 10,
            smellRange: 0, // No sense of smell
            primarySense: SensePriority.SIGHT,
            hungerRate: 0, // Undead don't need food
            thirstRate: 0, // Undead don't need water
            sleepHoursPerDay: 0, // Undead don't sleep
            minPackSize: 2,
            maxPackSize: 5,
            territoryRadius: 30,
            fearThreshold: 0.99, // Fearless
            fleeDistance: 0, // Never flee
            courageBonus: 0.3,
            humanFearFactor: 0, // Not afraid of humans
            combatPower: 22,
            aggroDistance: 15, // Aggro from far away
            breedingSeason: false,
            gestationTicks: 0,
            offspringCount: [0, 0],
            spriteSize: 1.0,
            animationSpeedMultiplier: 0.9,
            lootTable: [
                { itemType: ItemType.BONE, minAmount: 1, maxAmount: 3, chance: 1.0 },
                { itemType: ItemType.IRON_SWORD, minAmount: 1, maxAmount: 1, chance: 0.08 },
            ],
        },
    ],
]);
// ============================================================================
// Utility Functions
// ============================================================================
export const getNpcProperties = ({ npcType }) => {
    const props = NPC_DEFINITIONS.get(npcType);
    if (!props) {
        // Return default properties for unknown animals
        const rabbit = NPC_DEFINITIONS.get(NpcType.RABBIT);
        if (!rabbit) {
            throw new Error('RABBIT animal definition not found');
        }
        return rabbit;
    }
    return props;
};
export const getNpcName = ({ npcType }) => {
    return NPC_DEFINITIONS.get(npcType)?.name ?? 'Unknown Animal';
};
export const getAllNpcTypes = () => {
    return Array.from(NPC_DEFINITIONS.keys());
};
export const getNpcsByDiet = ({ diet }) => {
    return Array.from(NPC_DEFINITIONS.values()).filter((a) => a.diet === diet);
};
export const getNpcsBySocial = ({ social, }) => {
    return Array.from(NPC_DEFINITIONS.values()).filter((a) => a.social === social);
};
export const getPredators = () => {
    return Array.from(NPC_DEFINITIONS.values()).filter((a) => a.diet === DietType.CARNIVORE || a.aggression >= AggressionLevel.OPPORTUNISTIC);
};
export const getPrey = () => {
    return Array.from(NPC_DEFINITIONS.values()).filter((a) => a.diet === DietType.HERBIVORE && a.aggression <= AggressionLevel.DEFENSIVE);
};
//# sourceMappingURL=definitions.js.map